import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Plus, Edit, Trash2, MapPin, Route, ChevronRight, Calendar, Sparkles, GripVertical, ArrowUp, ArrowDown, FileSpreadsheet, Download, CheckCircle, AlertCircle } from 'lucide-react';

const ItineraryManager = () => {
  const [activeTab, setActiveTab] = useState('routes');
  const [routes, setRoutes] = useState([]);
  const [places, setPlaces] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedPlaceForItinerary, setSelectedPlaceForItinerary] = useState(null);
  const [routeItineraries, setRouteItineraries] = useState([]);
  const [placeHighlights, setPlaceHighlights] = useState([]);
  const [placeItineraries, setPlaceItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportType, setBulkImportType] = useState('route-itineraries'); // route-itineraries, place-highlights, place-itineraries
  const [importFile, setImportFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);

  // Dialog states
  const [isItineraryDialogOpen, setIsItineraryDialogOpen] = useState(false);
  const [isHighlightDialogOpen, setIsHighlightDialogOpen] = useState(false);
  const [isPlaceItineraryDialogOpen, setIsPlaceItineraryDialogOpen] = useState(false);
  const [editingItinerary, setEditingItinerary] = useState(null);
  const [editingHighlight, setEditingHighlight] = useState(null);
  const [editingPlaceItinerary, setEditingPlaceItinerary] = useState(null);

  // Form states
  const [itineraryForm, setItineraryForm] = useState({
    day_number: 1,
    day_title: '',
    itinerary_text: '',
    highlights: '',
    optional_activities: '',
  });
  const [highlightForm, setHighlightForm] = useState({
    highlight_text: '',
    display_order: 0,
  });
  const [placeItineraryForm, setPlaceItineraryForm] = useState({
    day_title: '',
    day_description: '',
    highlights: '',
    optional_activities: '',
    display_order: 0,
  });

  useEffect(() => {
    fetchRoutes();
    fetchPlaces();
  }, []);

  useEffect(() => {
    if (selectedRoute) {
      fetchRouteItineraries(selectedRoute.route_id);
    }
  }, [selectedRoute]);

  useEffect(() => {
    if (selectedPlace) {
      fetchPlaceHighlights(selectedPlace.place_id);
    }
  }, [selectedPlace]);

  useEffect(() => {
    if (selectedPlaceForItinerary) {
      fetchPlaceItineraries(selectedPlaceForItinerary.place_id);
    }
  }, [selectedPlaceForItinerary]);

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/routes');
      setRoutes(response.data);
      if (response.data.length > 0 && !selectedRoute) {
        setSelectedRoute(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load routes');
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data);
      if (response.data.length > 0 && !selectedPlace) {
        setSelectedPlace(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load places');
    } finally {
      setLoading(false);
    }
  };

  const fetchRouteItineraries = async (routeId) => {
    try {
      const response = await api.get(`/routes/${routeId}/itinerary`);
      setRouteItineraries(response.data);
    } catch (error) {
      setRouteItineraries([]);
    }
  };

  const fetchPlaceHighlights = async (placeId) => {
    try {
      const response = await api.get(`/places/${placeId}/highlights`);
      setPlaceHighlights(response.data);
    } catch (error) {
      setPlaceHighlights([]);
    }
  };

  const fetchPlaceItineraries = async (placeId) => {
    try {
      const response = await api.get(`/places/${placeId}/itineraries`);
      setPlaceItineraries(response.data);
    } catch (error) {
      setPlaceItineraries([]);
    }
  };

  // Place Itinerary handlers
  const openPlaceItineraryDialog = (itinerary = null) => {
    if (itinerary) {
      setEditingPlaceItinerary(itinerary);
      setPlaceItineraryForm({
        day_title: itinerary.day_title || '',
        day_description: itinerary.day_description || '',
        highlights: itinerary.highlights || '',
        optional_activities: itinerary.optional_activities || '',
        display_order: itinerary.display_order || 0,
      });
    } else {
      setEditingPlaceItinerary(null);
      setPlaceItineraryForm({
        day_title: `Explore ${selectedPlaceForItinerary?.place_name || ''}`,
        day_description: '',
        highlights: '',
        optional_activities: '',
        display_order: placeItineraries.length,
      });
    }
    setIsPlaceItineraryDialogOpen(true);
  };

  const handlePlaceItinerarySubmit = async () => {
    if (!placeItineraryForm.day_title) {
      toast.error('Day title is required');
      return;
    }

    try {
      if (editingPlaceItinerary) {
        await api.put(
          `/places/${selectedPlaceForItinerary.place_id}/itineraries/${editingPlaceItinerary.place_itinerary_id}`,
          placeItineraryForm
        );
        toast.success('Itinerary updated successfully');
      } else {
        await api.post(`/places/${selectedPlaceForItinerary.place_id}/itineraries`, placeItineraryForm);
        toast.success('Itinerary added successfully');
      }
      setIsPlaceItineraryDialogOpen(false);
      fetchPlaceItineraries(selectedPlaceForItinerary.place_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save itinerary');
    }
  };

  const handleDeletePlaceItinerary = async (itineraryId) => {
    if (!window.confirm('Are you sure you want to delete this itinerary?')) return;
    
    try {
      await api.delete(`/places/${selectedPlaceForItinerary.place_id}/itineraries/${itineraryId}`);
      toast.success('Itinerary deleted successfully');
      fetchPlaceItineraries(selectedPlaceForItinerary.place_id);
    } catch (error) {
      toast.error('Failed to delete itinerary');
    }
  };

  // Route Itinerary handlers
  const openItineraryDialog = (itinerary = null) => {
    if (itinerary) {
      setEditingItinerary(itinerary);
      setItineraryForm({
        day_number: itinerary.day_number,
        day_title: itinerary.day_title || '',
        itinerary_text: itinerary.itinerary_text || '',
        highlights: itinerary.highlights || '',
        optional_activities: itinerary.optional_activities || '',
      });
    } else {
      setEditingItinerary(null);
      const nextDay = routeItineraries.length > 0 
        ? Math.max(...routeItineraries.map(i => i.day_number)) + 1 
        : 1;
      setItineraryForm({
        day_number: nextDay,
        day_title: '',
        itinerary_text: '',
        highlights: '',
        optional_activities: '',
      });
    }
    setIsItineraryDialogOpen(true);
  };

  const handleItinerarySubmit = async () => {
    if (!itineraryForm.day_title) {
      toast.error('Day title is required');
      return;
    }

    try {
      if (editingItinerary) {
        await api.put(
          `/routes/${selectedRoute.route_id}/itinerary/${editingItinerary.itinerary_id}`,
          itineraryForm
        );
        toast.success('Itinerary updated successfully');
      } else {
        await api.post(`/routes/${selectedRoute.route_id}/itinerary`, itineraryForm);
        toast.success('Itinerary added successfully');
      }
      setIsItineraryDialogOpen(false);
      fetchRouteItineraries(selectedRoute.route_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save itinerary');
    }
  };

  const handleDeleteItinerary = async (itineraryId) => {
    if (!window.confirm('Are you sure you want to delete this day?')) return;
    
    try {
      await api.delete(`/routes/${selectedRoute.route_id}/itinerary/${itineraryId}`);
      toast.success('Day deleted successfully');
      fetchRouteItineraries(selectedRoute.route_id);
    } catch (error) {
      toast.error('Failed to delete itinerary');
    }
  };

  // Place Highlights handlers
  const openHighlightDialog = (highlight = null) => {
    if (highlight) {
      setEditingHighlight(highlight);
      setHighlightForm({
        highlight_text: highlight.highlight_text || '',
        display_order: highlight.display_order || 0,
      });
    } else {
      setEditingHighlight(null);
      setHighlightForm({
        highlight_text: '',
        display_order: placeHighlights.length,
      });
    }
    setIsHighlightDialogOpen(true);
  };

  const handleHighlightSubmit = async () => {
    if (!highlightForm.highlight_text) {
      toast.error('Highlight text is required');
      return;
    }

    try {
      if (editingHighlight) {
        await api.put(
          `/places/${selectedPlace.place_id}/highlights/${editingHighlight.highlight_id}`,
          highlightForm
        );
        toast.success('Highlight updated successfully');
      } else {
        await api.post(`/places/${selectedPlace.place_id}/highlights`, highlightForm);
        toast.success('Highlight added successfully');
      }
      setIsHighlightDialogOpen(false);
      fetchPlaceHighlights(selectedPlace.place_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save highlight');
    }
  };

  const handleDeleteHighlight = async (highlightId) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) return;
    
    try {
      await api.delete(`/places/${selectedPlace.place_id}/highlights/${highlightId}`);
      toast.success('Highlight deleted successfully');
      fetchPlaceHighlights(selectedPlace.place_id);
    } catch (error) {
      toast.error('Failed to delete highlight');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="itinerary-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Itinerary Master</h1>
            <p className="text-gray-600 mt-1">Create reusable day-wise itineraries for routes and places. These templates auto-populate when creating quotations.</p>
          </div>
          <Dialog open={bulkImportOpen} onOpenChange={(open) => { setBulkImportOpen(open); if (!open) { setImportFile(null); setValidationResult(null); setImportResult(null); if(importFileRef.current) importFileRef.current.value = ''; } }}>
            <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-itinerary-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Bulk Import</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Import Type Selector */}
                <div className="space-y-2">
                  <Label>Select Import Type</Label>
                  <Select value={bulkImportType} onValueChange={(v) => { setBulkImportType(v); setImportFile(null); setValidationResult(null); setImportResult(null); if(importFileRef.current) importFileRef.current.value = ''; }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="route-itineraries">Route Itineraries</SelectItem>
                      <SelectItem value="place-highlights">Place Highlights</SelectItem>
                      <SelectItem value="place-itineraries">Place Itineraries</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Download Template</p>
                    <p className="text-xs text-gray-500">
                      {bulkImportType === 'route-itineraries' && 'CSV with route_code, day_title, day_description, highlights, optional_activities'}
                      {bulkImportType === 'place-highlights' && 'CSV with place_name, state, highlight_text, display_order'}
                      {bulkImportType === 'place-itineraries' && 'CSV with place_name, state, day_title, day_description, highlights, optional_activities'}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const response = await api.get(`/admin/bulk-import/${bulkImportType}/template`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `${bulkImportType}_template.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      toast.error('Failed to download template');
                    }
                  }}>
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label>Upload File (CSV or Excel)</Label>
                  <Input
                    ref={importFileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setImportFile(file);
                      setValidationResult(null);
                      setImportResult(null);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await api.post(`/admin/bulk-import/${bulkImportType}/validate`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setValidationResult(response.data);
                      } catch (error) {
                        toast.error('Failed to validate file');
                        setValidationResult({ valid: false, errors: [error.response?.data?.detail || 'Validation failed'] });
                      }
                    }}
                  />
                </div>
                
                {validationResult && (
                  <div className={`p-3 rounded-lg ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {validationResult.valid ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-red-600" />}
                      <span className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                        {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{validationResult.valid_rows} of {validationResult.total_rows} rows valid</p>
                    {validationResult.errors?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                        {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                
                {importResult && (
                  <div className={`p-3 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`font-medium ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {importResult.success ? `Created: ${importResult.created} records` : 'Import Failed'}
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkImportOpen(false)}>Close</Button>
                <Button 
                  onClick={async () => {
                    if (!importFile || !validationResult?.valid) return;
                    setImporting(true);
                    const fd = new FormData();
                    fd.append('file', importFile);
                    try {
                      const response = await api.post(`/admin/bulk-import/${bulkImportType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setImportResult(response.data);
                      if (response.data.success) {
                        toast.success(`Imported ${response.data.created} records`);
                        // Refresh relevant data
                        if (bulkImportType === 'route-itineraries' && selectedRoute) {
                          fetchRouteItineraries(selectedRoute.route_id);
                        } else if (bulkImportType === 'place-highlights' && selectedPlace) {
                          fetchPlaceHighlights(selectedPlace.place_id);
                        } else if (bulkImportType === 'place-itineraries' && selectedPlaceForItinerary) {
                          fetchPlaceItineraries(selectedPlaceForItinerary.place_id);
                        }
                      }
                    } catch (error) {
                      toast.error('Import failed');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!validationResult?.valid || importing}
                >
                  {importing ? 'Importing...' : 'Import'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-xl">
            <TabsTrigger value="routes" data-testid="routes-tab">
              <Route className="w-4 h-4 mr-2" />
              Route Itineraries
            </TabsTrigger>
            <TabsTrigger value="places" data-testid="places-tab">
              <MapPin className="w-4 h-4 mr-2" />
              Place Highlights
            </TabsTrigger>
            <TabsTrigger value="place-itineraries" data-testid="place-itineraries-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Place Itineraries
            </TabsTrigger>
          </TabsList>

          {/* Route Itineraries Tab */}
          <TabsContent value="routes" className="space-y-4 mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Route Itineraries:</strong> Define day-by-day activities for each fixed route. When a quotation uses this route, the itinerary auto-fills with these descriptions.
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Route Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Select Route</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {routes.map((route) => (
                    <div
                      key={route.route_id}
                      onClick={() => setSelectedRoute(route)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedRoute?.route_id === route.route_id
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`route-${route.route_id}`}
                    >
                      <p className="font-medium text-sm">{route.route_code}</p>
                      <p className="text-xs text-gray-600">{route.route_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {route.total_days}D / {route.total_nights}N
                      </p>
                      {/* Display route with nights at each place */}
                      {route.stops && route.stops.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-2 text-xs">
                          {route.stops.map((stop, idx) => (
                            <React.Fragment key={stop.place_id}>
                              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                {stop.place_name} {stop.nights > 0 && <span className="font-semibold">({stop.nights}N)</span>}
                              </span>
                              {idx < route.stops.length - 1 && (
                                <ChevronRight className="w-3 h-3 text-gray-400" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Itinerary Editor */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {selectedRoute ? `${selectedRoute.route_code} - Day Wise Itinerary` : 'Select a Route'}
                    </CardTitle>
                    {selectedRoute && (
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedRoute.stops?.map(s => s.place_name).join(' → ')} • {selectedRoute.total_days} Days
                      </p>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedRoute ? (
                    <div className="text-center py-8 text-gray-500">
                      Select a route to manage its itinerary
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Generate day blocks based on total_days */}
                      {Array.from({ length: selectedRoute.total_days }, (_, idx) => {
                        const dayNum = idx + 1;
                        const existingItinerary = routeItineraries.find(i => i.day_number === dayNum);
                        const isFilled = existingItinerary && existingItinerary.itinerary_text;
                        
                        return (
                          <div
                            key={dayNum}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              isFilled 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-gray-50 border-gray-200 border-dashed'
                            }`}
                            data-testid={`day-block-${dayNum}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                                    isFilled 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    Day {dayNum}
                                  </span>
                                  {existingItinerary?.day_title && (
                                    <span className="text-sm font-medium text-gray-700">
                                      {existingItinerary.day_title}
                                    </span>
                                  )}
                                  {isFilled && (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  )}
                                </div>
                                {isFilled ? (
                                  <>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap mb-2 line-clamp-3">
                                      {existingItinerary.itinerary_text}
                                    </p>
                                    {existingItinerary.highlights && (
                                      <div className="mt-2">
                                        <p className="text-xs font-medium text-blue-700">Highlights:</p>
                                        <p className="text-xs text-gray-600 line-clamp-2">{existingItinerary.highlights}</p>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-400 italic">No content added yet</p>
                                )}
                              </div>
                              <div className="ml-4 flex gap-2">
                                <Button
                                  variant={isFilled ? "outline" : "default"}
                                  size="sm"
                                  onClick={() => {
                                    if (existingItinerary) {
                                      openItineraryDialog(existingItinerary);
                                    } else {
                                      // Open dialog for new day with pre-filled day_number
                                      setEditingItinerary(null);
                                      setItineraryForm({
                                        day_number: dayNum,
                                        day_title: '',
                                        itinerary_text: '',
                                        highlights: '',
                                        optional_activities: '',
                                      });
                                      setIsItineraryDialogOpen(true);
                                    }
                                  }}
                                  data-testid={`day-${dayNum}-btn`}
                                >
                                  {isFilled ? (
                                    <>
                                      <Edit className="w-4 h-4 mr-1" />
                                      Edit
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="w-4 h-4 mr-1" />
                                      Add
                                    </>
                                  )}
                                </Button>
                                {isFilled && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteItinerary(existingItinerary.itinerary_id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Progress indicator */}
                      {selectedRoute && (
                        <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Completion Progress</span>
                            <span className="text-sm text-gray-600">
                              {routeItineraries.filter(i => i.itinerary_text).length} / {selectedRoute.total_days} days
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${(routeItineraries.filter(i => i.itinerary_text).length / selectedRoute.total_days) * 100}%` 
                              }}
                            />
                          </div>
                          {routeItineraries.filter(i => i.itinerary_text).length === selectedRoute.total_days ? (
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              All days completed! This route itinerary is ready to use.
                            </p>
                          ) : (
                            <p className="text-xs text-amber-600 mt-2">
                              Please fill all {selectedRoute.total_days} days to complete this route itinerary.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          {/* Place Highlights Tab */}
          <TabsContent value="places" className="space-y-4 mt-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <strong>Place Highlights:</strong> Add key attractions and must-see spots for each destination. These appear as bullet points in the quotation's sketch itinerary.
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Place Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Select Place</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {places.map((place) => (
                    <div
                      key={place.place_id}
                      onClick={() => setSelectedPlace(place)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedPlace?.place_id === place.place_id
                          ? 'bg-green-50 border-2 border-green-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`place-${place.place_id}`}
                    >
                      <p className="font-medium text-sm">{place.place_name}</p>
                      <p className="text-xs text-gray-600">{place.state}</p>
                      <p className="text-xs text-gray-500">{place.place_type}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Highlights Editor */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {selectedPlace ? `${selectedPlace.place_name} - Highlights` : 'Select a Place'}
                    </CardTitle>
                    {selectedPlace && (
                      <p className="text-sm text-gray-600 mt-1">
                        Things to do & see in {selectedPlace.place_name}
                      </p>
                    )}
                  </div>
                  {selectedPlace && (
                    <Button onClick={() => openHighlightDialog()} data-testid="add-highlight-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Highlight
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {!selectedPlace ? (
                    <div className="text-center py-8 text-gray-500">
                      Select a place to manage its highlights
                    </div>
                  ) : placeHighlights.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No highlights added yet</p>
                      <p className="text-sm">Click "Add Highlight" to add activities and attractions</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {placeHighlights
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((highlight, index) => (
                          <div
                            key={highlight.highlight_id}
                            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                            data-testid={`highlight-${highlight.highlight_id}`}
                          >
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                              {index + 1}
                            </span>
                            <p className="flex-1 text-sm text-gray-700">{highlight.highlight_text}</p>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openHighlightDialog(highlight)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => handleDeleteHighlight(highlight.highlight_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Place Itineraries Tab */}
          <TabsContent value="place-itineraries" className="space-y-4 mt-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
              <strong>Place Itineraries:</strong> Create multi-day itinerary templates for destinations. Used when building custom packages with variable nights at each place.
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Place Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Select Place</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {places.map((place) => (
                    <div
                      key={place.place_id}
                      onClick={() => setSelectedPlaceForItinerary(place)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedPlaceForItinerary?.place_id === place.place_id
                          ? 'bg-purple-50 border-2 border-purple-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`place-itinerary-${place.place_id}`}
                    >
                      <p className="font-medium text-sm">{place.place_name}</p>
                      <p className="text-xs text-gray-600">{place.state}</p>
                      <p className="text-xs text-gray-500">{place.place_type}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Place Itineraries Editor */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {selectedPlaceForItinerary ? `${selectedPlaceForItinerary.place_name} - Day Itineraries` : 'Select a Place'}
                    </CardTitle>
                    {selectedPlaceForItinerary && (
                      <p className="text-sm text-gray-600 mt-1">
                        Full day activities for Custom Packages
                      </p>
                    )}
                  </div>
                  {selectedPlaceForItinerary && (
                    <Button onClick={() => openPlaceItineraryDialog()} data-testid="add-place-itinerary-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Day Itinerary
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {!selectedPlaceForItinerary ? (
                    <div className="text-center py-8 text-gray-500">
                      Select a place to manage its day itineraries
                    </div>
                  ) : placeItineraries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No day itineraries added yet</p>
                      <p className="text-sm">Click "Add Day Itinerary" to create content for Custom Packages</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {placeItineraries
                        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                        .map((itinerary, index) => (
                          <div
                            key={itinerary.place_itinerary_id}
                            className="p-4 bg-purple-50 rounded-lg border border-purple-200"
                            data-testid={`place-itinerary-${itinerary.place_itinerary_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-medium">
                                    Option {index + 1}
                                  </span>
                                  <h4 className="font-semibold text-purple-900">{itinerary.day_title}</h4>
                                </div>
                                {itinerary.day_description && (
                                  <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">
                                    {itinerary.day_description}
                                  </p>
                                )}
                                {itinerary.highlights && (
                                  <div className="mt-2">
                                    <p className="text-xs font-medium text-purple-700">Highlights:</p>
                                    <p className="text-xs text-gray-600">{itinerary.highlights}</p>
                                  </div>
                                )}
                                {itinerary.optional_activities && (
                                  <div className="mt-2">
                                    <p className="text-xs font-medium text-purple-700">Optional Activities:</p>
                                    <p className="text-xs text-gray-600">{itinerary.optional_activities}</p>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPlaceItineraryDialog(itinerary)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600"
                                  onClick={() => handleDeletePlaceItinerary(itinerary.place_itinerary_id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Itinerary Dialog */}
        <Dialog open={isItineraryDialogOpen} onOpenChange={setIsItineraryDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingItinerary ? 'Edit Day Itinerary' : 'Add Day Itinerary'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="day_title">
                  Day Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="day_title"
                  value={itineraryForm.day_title}
                  onChange={(e) => setItineraryForm(prev => ({
                    ...prev,
                    day_title: e.target.value
                  }))}
                  placeholder="e.g., Explore Munnar"
                  data-testid="day-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="itinerary_text">
                  Day Description
                </Label>
                <Textarea
                  id="itinerary_text"
                  value={itineraryForm.itinerary_text}
                  onChange={(e) => setItineraryForm(prev => ({
                    ...prev,
                    itinerary_text: e.target.value
                  }))}
                  placeholder="Begin your journey with a visit to the vast Tea Plantations and explore the Tea Museum..."
                  rows={4}
                  data-testid="itinerary-text-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="highlights">
                  Highlights (comma separated)
                </Label>
                <Input
                  id="highlights"
                  value={itineraryForm.highlights}
                  onChange={(e) => setItineraryForm(prev => ({
                    ...prev,
                    highlights: e.target.value
                  }))}
                  placeholder="e.g., Tea Museum, Mattupetty Dam, Echo Point, Kundala Lake"
                  data-testid="highlights-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="optional_activities">
                  Optional Activities
                </Label>
                <Input
                  id="optional_activities"
                  value={itineraryForm.optional_activities}
                  onChange={(e) => setItineraryForm(prev => ({
                    ...prev,
                    optional_activities: e.target.value
                  }))}
                  placeholder="e.g., Short speed boat ride at Mattupetty / Horse ride at Echo Point"
                  data-testid="optional-activities-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="day_number">Display Order</Label>
                <Input
                  id="day_number"
                  type="number"
                  min="1"
                  value={itineraryForm.day_number}
                  onChange={(e) => setItineraryForm(prev => ({
                    ...prev,
                    day_number: parseInt(e.target.value) || 1
                  }))}
                  className="w-24"
                  data-testid="day-number-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsItineraryDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleItinerarySubmit} data-testid="save-itinerary-btn">
                {editingItinerary ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Highlight Dialog */}
        <Dialog open={isHighlightDialogOpen} onOpenChange={setIsHighlightDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingHighlight ? 'Edit Highlight' : 'Add Highlight'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="highlight_text">
                  Highlight <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="highlight_text"
                  value={highlightForm.highlight_text}
                  onChange={(e) => setHighlightForm(prev => ({
                    ...prev,
                    highlight_text: e.target.value
                  }))}
                  placeholder="e.g., Visit the famous Taj Mahal at sunrise"
                  rows={3}
                  data-testid="highlight-text-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  min="0"
                  value={highlightForm.display_order}
                  onChange={(e) => setHighlightForm(prev => ({
                    ...prev,
                    display_order: parseInt(e.target.value) || 0
                  }))}
                  data-testid="display-order-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsHighlightDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleHighlightSubmit} data-testid="save-highlight-btn">
                {editingHighlight ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Place Itinerary Dialog */}
        <Dialog open={isPlaceItineraryDialogOpen} onOpenChange={setIsPlaceItineraryDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingPlaceItinerary ? 'Edit Day Itinerary' : 'Add Day Itinerary'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="place_day_title">
                  Day Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="place_day_title"
                  value={placeItineraryForm.day_title}
                  onChange={(e) => setPlaceItineraryForm(prev => ({
                    ...prev,
                    day_title: e.target.value
                  }))}
                  placeholder="e.g., Explore Munnar"
                  data-testid="place-day-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="place_day_description">
                  Day Description
                </Label>
                <Textarea
                  id="place_day_description"
                  value={placeItineraryForm.day_description}
                  onChange={(e) => setPlaceItineraryForm(prev => ({
                    ...prev,
                    day_description: e.target.value
                  }))}
                  placeholder="Begin your journey with a visit to the vast Tea Plantations and explore the Tea Museum..."
                  rows={4}
                  data-testid="place-day-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="place_highlights">
                  Highlights (comma separated)
                </Label>
                <Input
                  id="place_highlights"
                  value={placeItineraryForm.highlights}
                  onChange={(e) => setPlaceItineraryForm(prev => ({
                    ...prev,
                    highlights: e.target.value
                  }))}
                  placeholder="e.g., Tea Museum, Mattupetty Dam, Echo Point, Kundala Lake"
                  data-testid="place-highlights-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="place_optional">
                  Optional Activities
                </Label>
                <Input
                  id="place_optional"
                  value={placeItineraryForm.optional_activities}
                  onChange={(e) => setPlaceItineraryForm(prev => ({
                    ...prev,
                    optional_activities: e.target.value
                  }))}
                  placeholder="e.g., Short speed boat ride at Mattupetty / Horse ride at Echo Point"
                  data-testid="place-optional-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="place_display_order">Display Order</Label>
                <Input
                  id="place_display_order"
                  type="number"
                  min="0"
                  value={placeItineraryForm.display_order}
                  onChange={(e) => setPlaceItineraryForm(prev => ({
                    ...prev,
                    display_order: parseInt(e.target.value) || 0
                  }))}
                  className="w-24"
                  data-testid="place-display-order-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPlaceItineraryDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handlePlaceItinerarySubmit} data-testid="save-place-itinerary-btn">
                {editingPlaceItinerary ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ItineraryManager;
